import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { Image as ImageIcon, Check, Upload, Trash2 } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { NS, ORG_SETTINGS_KEYS } from '../../../../../../i18n/constants/keys';

const DEFAULT_LOGO = '/logo.png'; // Default HiAcuity logo path

type UploadBase64ImageProps = {
  logo: string;
  setLogo: (base64Logo: string) => void;
  isEditing: boolean;
};

export const UploadBase64Image = ({
  logo,
  setLogo,
  isEditing,
}: UploadBase64ImageProps) => {
  const { t } = useTranslation(NS.organizationSettings);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const validateFile = async (
    file: File,
  ): Promise<{ isValid: boolean; error?: string }> => {
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Reset previous errors
    setUploadError(null);

    if (!validTypes.includes(file.type)) {
      const error = t(ORG_SETTINGS_KEYS.logo.invalidFileType);
      setUploadError(error);
      return { isValid: false, error };
    }

    if (file.size > maxSize) {
      const error = t(ORG_SETTINGS_KEYS.logo.fileTooLarge);
      setUploadError(error);
      return { isValid: false, error };
    }

    return { isValid: true };
  };

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setIsLoading(true);

    try {
      const file = e.dataTransfer.files[0];
      const validation = await validateFile(file);

      if (!validation.isValid) {
        toast.error(validation.error, { position: 'bottom-center' });
        return;
      }

      const dataUrl = await readFileAsDataURL(file);
      setLogo(dataUrl);
      setUploadError(null);
      toast.success(t(ORG_SETTINGS_KEYS.logo.uploadSuccess), {
        position: 'bottom-center',
      });
    } catch (error) {
      toast.error(t(ORG_SETTINGS_KEYS.logo.uploadFailed), {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsLoading(true);

      try {
        const file = e.target.files[0];
        const validation = await validateFile(file);

        if (!validation.isValid) {
          toast.error(validation.error, { position: 'bottom-center' });
          return;
        }

        const dataUrl = await readFileAsDataURL(file);
        setLogo(dataUrl);
        setUploadError(null);
        toast.success(t(ORG_SETTINGS_KEYS.logo.uploadSuccess), {
          position: 'bottom-center',
        });
      } catch (error) {
        toast.error(t(ORG_SETTINGS_KEYS.logo.uploadFailed), {
          position: 'bottom-center',
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper
      component={motion.div}
      layout
      variant='outlined'
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        p: 2.5,
        borderRadius: '16px',
        transition: 'all 0.3s ease',
        borderColor: isDragging ? 'primary.main' : 'divider',
        bgcolor: isDragging ? 'rgba(9, 55, 37, 0.04)' : '#ffffff',
      }}
    >
      <input
        type='file'
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept='.jpg,.jpeg,.png,.svg'
      />
      {logo ? (
        <Stack spacing={2.5} sx={{ width: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 80,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
              bgcolor: 'var(--brand-primary)',
              transition: 'background-color 0.3s',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.1), rgba(255,255,255,0.1))',
                pointerEvents: 'none',
              }}
            />
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              </Box>
            ) : (
              <Box
                component='img'
                src={logo || DEFAULT_LOGO}
                alt={t(ORG_SETTINGS_KEYS.logo.companyLogoAlt)}
                sx={{
                  maxWidth: 170,
                  maxHeight: 46,
                  objectFit: 'contain',
                  mx: 'auto',
                  transition: 'all 0.3s ease',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                  opacity: !logo ? 0.75 : 1,
                }}
              />
            )}
          </Box>

          <Stack spacing={1.5}>
            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t(ORG_SETTINGS_KEYS.labels.logoPreview)}
            </Typography>

            {isEditing && (
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type='button'
                    onClick={handleClick}
                    sx={{
                      flex: 1,
                      px: 1.5,
                      py: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: '#ffffff',
                      color: 'text.primary',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                      },
                    }}
                  >
                    <Upload size={14} />
                    <span>{t(ORG_SETTINGS_KEYS.logo.changeLogo)}</span>
                  </Box>

                  <Box
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type='button'
                    onClick={() => setLogo('')}
                    sx={{
                      px: 1.5,
                      py: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'error.light',
                      bgcolor: 'rgba(239, 68, 68, 0.05)',
                      color: 'error.main',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <Trash2 size={14} />
                    <span>{t(ORG_SETTINGS_KEYS.logo.remove)}</span>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      checked={!logo}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLogo(DEFAULT_LOGO);
                        }
                      }}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {t(ORG_SETTINGS_KEYS.logo.useDefaultCheckbox)}
                      </Typography>
                      <Box
                        component='img'
                        src={DEFAULT_LOGO}
                        alt={t(ORG_SETTINGS_KEYS.logo.defaultLogoAlt)}
                        sx={{ height: 14, opacity: 0.75 }}
                      />
                    </Box>
                  }
                  sx={{ m: 0, pt: 0.5 }}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ alignItems: 'center', py: 2, px: 1, textAlign: 'center', width: '100%' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              bgcolor: 'rgba(9, 55, 37, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageIcon size={28} color='#093725' />
          </Box>

          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Typography variant='caption' sx={{ color: 'text.primary', fontWeight: 700 }}>
              {t(ORG_SETTINGS_KEYS.logo.dragDrop)}
            </Typography>

            {uploadError && (
              <Alert severity='error' sx={{ borderRadius: '12px', fontSize: '0.75rem', py: 0.5 }}>
                {uploadError}
              </Alert>
            )}

            <Paper
              variant='outlined'
              sx={{
                bgcolor: 'rgba(248, 250, 252, 0.8)',
                borderRadius: '12px',
                p: 1.5,
                textAlign: 'left',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant='caption'
                sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1, fontSize: '0.625rem' }}
              >
                {t(ORG_SETTINGS_KEYS.logo.requirementsTitle)}
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Check size={12} color='#093725' />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                    {t(ORG_SETTINGS_KEYS.logo.formatRequirement)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Check size={12} color='#093725' />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                    {t(ORG_SETTINGS_KEYS.logo.dimensionRequirement)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Check size={12} color='#093725' />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                    {t(ORG_SETTINGS_KEYS.logo.fileSizeRequirement)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>

          <Stack spacing={1} sx={{ width: '100%', pt: 0.5 }}>
            <Box
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type='button'
              onClick={handleClick}
              sx={{
                width: '100%',
                py: 1,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: '#093725',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: '#041d13',
                },
              }}
            >
              {t(ORG_SETTINGS_KEYS.logo.uploadCustom)}
            </Box>

            <Box
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type='button'
              onClick={() => setLogo(DEFAULT_LOGO)}
              sx={{
                width: '100%',
                py: 1,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: 'rgba(241, 245, 249, 1)',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'rgba(226, 232, 240, 1)',
                },
              }}
            >
              {t(ORG_SETTINGS_KEYS.logo.useDefaultButton)}
            </Box>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};
